import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase, COUNTRY_NAMES_LV } from "@vinoga/db";
import {
  ArrowLeft,
  Save,
  Package,
  Globe,
  CheckCircle2,
  ChevronsUpDown,
  Tag,
  Layers,
  Wine,
  Check,
  BookOpen,
  ExternalLink,
  Image as ImageIcon,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n";
import { CategoryTreePicker } from "@/components/CategoryTreePicker";
import { FileUpload } from "@/components/admin/FileUpload";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { SlugInput } from "@/components/admin/SlugInput";
import { mediaSrc } from "@/lib/media";
import { storefrontProductUrl } from "@/lib/storefront";

const inputClass =
  "w-full bg-card border border-border rounded-lg px-3 py-2 font-body text-sm text-foreground outline-none focus:border-primary placeholder:text-muted-foreground";
const labelClass = "font-body text-xs font-medium text-muted-foreground mb-1 block";
const sectionClass = "bg-card border border-border rounded-lg p-5 space-y-4";

type Locale = "lv" | "en" | "ru";

type CategoryRow = {
  id: string;
  parent_id: string | null;
  slug: string;
  active: boolean;
  translations: any;
};
type PriceListRow = { id: string; code: string; name: string; position: number };
type InventoryLocationRow = { id: string; code: string; name: string; type: "shop" | "warehouse" };
type GrapeVarietyRow = { id: string; slug: string; translations: any };

// One LocaleFields per supported locale. Includes the seven descriptive
// content blocks the storefront PDP renders (Garšas piezīmes / Ražotājs /
// Vīnogas / Glabāšana / Ēdienu saderība / Apbalvojumi). All optional —
// stripEmpty() drops blanks before save so JSON stays tidy.
// Retired fields (2026-10-10): tasteDescription (Garša → foodPairing) and
// producerDescription (Par ražotāju → merged into description, the surviving
// "Pilnais apraksts"). Raw originals stay archived in products.attributes.
type LocaleFields = {
  name: string;
  slug: string;
  shortDescription: string;
  description: string;
  metaTitle: string;
  metaDescription: string;
  grapeDescription: string;
  storageDescription: string;
  smellDescription: string;
  awards: string;
  foodPairing: string;
};
const emptyLocale = (): LocaleFields => ({
  name: "",
  slug: "",
  shortDescription: "",
  description: "",
  metaTitle: "",
  metaDescription: "",
  grapeDescription: "",
  storageDescription: "",
  smellDescription: "",
  awards: "",
  foodPairing: "",
});

const LOCALES_KEYS: Array<{ id: Locale; key: string; fallback: string }> = [
  { id: "lv", key: "productEditor.tabLv", fallback: "Latviešu (default)" },
  { id: "en", key: "productEditor.tabEn", fallback: "English" },
  { id: "ru", key: "productEditor.tabRu", fallback: "Русский" },
];

export function ProductEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const { toast } = useToast();
  const tr = useT();

  const [activeTab, setActiveTab] = useState<Locale>("lv");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const [sku, setSku] = useState("");
  const [slug, setSlug] = useState("");
  const [translations, setTranslations] = useState<Record<Locale, LocaleFields>>({
    lv: emptyLocale(),
    en: emptyLocale(),
    ru: emptyLocale(),
  });
  const [abv, setAbv] = useState("");
  const [volumeMl, setVolumeMl] = useState("");
  // Volume vs weight (owner 2026-10-12): the amount input binds to volumeMl
  // or weightG per measureType; measureDisplay "auto" = NULL (today's
  // automatic CL/L, or G/KG). Unit price follows the type (€/L or €/KG).
  const [measureType, setMeasureType] = useState<"volume" | "weight">("volume");
  const [weightG, setWeightG] = useState("");
  const [measureDisplay, setMeasureDisplay] = useState<string>("auto");
  const [vintage, setVintage] = useState("");
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [producer, setProducer] = useState("");
  const [isAlcohol, setIsAlcohol] = useState(true);
  const [requiresDeposit, setRequiresDeposit] = useState(false);
  const [active, setActive] = useState(true);
  const [featured, setFeatured] = useState(false);
  // "Piezīmes" — the product's tags (curated in panelis /tags), shown as
  // colored chips on the product image in the storefront. Persisted to
  // product_tags; free-text labels were the wrong model (owner 2026-10-10).
  const [tagOptions, setTagOptions] = useState<
    Array<{ id: string; slug: string; name: string; color: string | null }>
  >([]);
  const [tagIds, setTagIds] = useState<Set<string>>(new Set());
  const [barcode, setBarcode] = useState("");
  const [serveTemp, setServeTemp] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  // Primary product image — backed by a single `product_images` row at
  // position 0. Legacy imports stored bare R2 keys (no protocol, with
  // spaces); we resolve to a full URL on load via mediaSrc so the
  // ImageUpload preview can render it. On save we persist the value as-is
  // (which converts legacy bare keys into full URLs the first time a
  // product is edited — storefront's `imageSrc` passes through full URLs).
  const [imageUrl, setImageUrl] = useState("");
  const [strengthScale, setStrengthScale] = useState<string>("");
  const [typeScale, setTypeScale] = useState<string>("");
  const [countryOpen, setCountryOpen] = useState(false);
  const [regionOpen, setRegionOpen] = useState(false);
  // Free-text fallback for regions not in `regionOptions` — entered via the
  // combobox's "Use \"<query>\"" item. Stored separately so we don't pollute
  // the source list; persisted to the product on save via `region` state.
  const [regionQuery, setRegionQuery] = useState("");

  const [categories, setCategories] = useState<CategoryRow[]>([]);
  // Multi-category (Prestashop-style): the checked set + which one is MAIN
  // (persisted at product_categories.position 0 → storefront breadcrumb).
  const [categoryIds, setCategoryIds] = useState<Set<string>>(new Set());
  const [mainCategoryId, setMainCategoryId] = useState<string | null>(null);

  const [priceLists, setPriceLists] = useState<PriceListRow[]>([]);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [salePrice, setSalePrice] = useState("");

  const [locations, setLocations] = useState<InventoryLocationRow[]>([]);
  const [stock, setStock] = useState<Record<string, string>>({});

  // Recommendation picks (Sept 9 report #11) — ordered manual lists persisted
  // to similar_products (PDP cross-sell) / associated_products (cart upsell).
  const [pickerProducts, setPickerProducts] = useState<
    Array<{ id: string; sku: string; name: string }>
  >([]);
  const [similarIds, setSimilarIds] = useState<string[]>([]);
  const [associatedIds, setAssociatedIds] = useState<string[]>([]);

  // Grape varieties + dropdown sources for country/region
  const [grapeOptions, setGrapeOptions] = useState<GrapeVarietyRow[]>([]);
  const [grapeIds, setGrapeIds] = useState<Set<string>>(new Set());
  const [countryOptions, setCountryOptions] = useState<string[]>([]);
  const [regionOptions, setRegionOptions] = useState<string[]>([]);

  // Lookups
  useEffect(() => {
    (async () => {
      const [c, pl, loc, gv, allProducts, tg] = await Promise.all([
        supabase
          .from("categories")
          .select("id, parent_id, slug, translations, active")
          .order("position"),
        supabase.from("price_lists").select("id, code, name, position").order("position"),
        supabase.from("inventory_locations").select("id, code, name, type").order("position"),
        supabase
          .from("grape_varieties")
          .select("id, slug, translations")
          .eq("active", true)
          .order("slug"),
        supabase.from("products").select("id, sku, translations, country, region").limit(2000),
        supabase
          .from("tags")
          .select("id, slug, translations, color")
          .eq("active", true)
          .order("position"),
      ]);
      setTagOptions(
        ((tg.data ?? []) as any[]).map((t) => ({
          id: t.id,
          slug: t.slug,
          name: t.translations?.lv?.name ?? t.slug,
          color: t.color ?? null,
        })),
      );
      setCategories(c.data ?? []);
      setPriceLists(pl.data ?? []);
      setLocations(loc.data ?? []);
      setGrapeOptions(gv.data ?? []);
      // Derive distinct country / region lists from existing catalog so the
      // admin sees a curated set instead of typing freeform. New values can
      // still be entered — the combobox falls through to free text.
      const countries = new Set<string>();
      const regions = new Set<string>();
      (allProducts.data ?? []).forEach((p: any) => {
        if (p.country) countries.add(p.country);
        if (p.region) regions.add(p.region);
      });
      setCountryOptions(Array.from(countries).sort());
      setRegionOptions(Array.from(regions).sort());
      setPickerProducts(
        (allProducts.data ?? [])
          .map((p: any) => ({
            id: p.id,
            sku: String(p.sku ?? ""),
            name: p.translations?.lv?.name ?? p.sku ?? "",
          }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name)),
      );
    })();
  }, []);

  // Load existing product
  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      const { data: p } = await supabase
        .from("products")
        .select("*")
        .eq("id", id!)
        .single();
      if (!p) {
        toast({
          title: tr("common.notFound", "Not found"),
          description: tr("productEditor.notFound", "Product not found"),
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      setSku(p.sku ?? "");
      setSlug(p.slug ?? "");
      const stored = (p.translations ?? {}) as Record<Locale, Partial<LocaleFields>>;
      // Drop the retired keys (producerDescription / tasteDescription) on
      // load so a save also scrubs them from rows the data cleanup missed —
      // otherwise the spread would carry them back forever.
      const load = (raw: Partial<LocaleFields> | undefined): LocaleFields => {
        const { producerDescription: _p, tasteDescription: _t, ...rest } =
          (raw ?? {}) as Partial<LocaleFields> & {
            producerDescription?: string;
            tasteDescription?: string;
          };
        return { ...emptyLocale(), ...rest } as LocaleFields;
      };
      setTranslations({
        lv: load(stored.lv),
        en: load(stored.en),
        ru: load(stored.ru),
      });
      setAbv(p.abv ?? "");
      setVolumeMl(p.volume_ml != null ? String(p.volume_ml) : "");
      setMeasureType(p.measure_type === "weight" ? "weight" : "volume");
      setWeightG(p.weight_g != null ? String(p.weight_g) : "");
      setMeasureDisplay(p.measure_display ?? "auto");
      setVintage(p.vintage != null ? String(p.vintage) : "");
      setCountry(p.country ?? "");
      setRegion(p.region ?? "");
      setProducer(p.producer ?? "");
      setIsAlcohol(!!p.is_alcohol);
      setRequiresDeposit(!!p.requires_deposit);
      setActive(!!p.active);
      setFeatured(!!p.featured);
      setBarcode(p.barcode ?? "");
      setServeTemp(p.serve_temp ?? "");
      setPdfUrl(p.pdf_url ?? "");
      setVideoUrl(p.video_url ?? "");
      setStrengthScale(p.strength_scale != null ? String(p.strength_scale) : "");
      setTypeScale(p.type_scale != null ? String(p.type_scale) : "");

      const [pcat, ppr, pst, pgv, pimg, psim, passoc, ptg] = await Promise.all([
        supabase
          .from("product_categories")
          .select("category_id, position")
          .eq("product_id", id!)
          .order("position"),
        supabase.from("product_prices").select("price_list_id, amount").eq("product_id", id!),
        supabase.from("inventory_stock").select("location_id, qty").eq("product_id", id!),
        supabase.from("product_grape_varieties").select("grape_variety_id").eq("product_id", id!),
        supabase
          .from("product_images")
          .select("url, position")
          .eq("product_id", id!)
          .order("position", { ascending: true })
          .limit(1),
        supabase
          .from("similar_products")
          .select("similar_product_id, sort_order")
          .eq("product_id", id!)
          .order("sort_order"),
        supabase
          .from("associated_products")
          .select("associated_product_id, sort_order")
          .eq("product_id", id!)
          .order("sort_order"),
        supabase.from("product_tags").select("tag_id").eq("product_id", id!),
      ]);
      setTagIds(new Set(((ptg.data ?? []) as any[]).map((r) => r.tag_id)));
      const catRows = (pcat.data ?? []) as Array<{ category_id: string; position: number }>;
      setCategoryIds(new Set(catRows.map((r) => r.category_id)));
      setMainCategoryId(catRows[0]?.category_id ?? null);
      const pmap: Record<string, string> = {};
      (ppr.data ?? []).forEach((r: any) => (pmap[r.price_list_id] = r.amount));
      setPrices(pmap);
      setSalePrice(p.sale_price != null ? String(p.sale_price) : "");
      const smap: Record<string, string> = {};
      (pst.data ?? []).forEach((r: any) => (smap[r.location_id] = String(r.qty)));
      setStock(smap);
      setGrapeIds(new Set((pgv.data ?? []).map((r: any) => r.grape_variety_id)));
      const rawImg = (pimg.data?.[0]?.url ?? "") as string;
      setImageUrl(rawImg ? (mediaSrc(rawImg) ?? rawImg) : "");
      setSimilarIds((psim.data ?? []).map((r: any) => r.similar_product_id));
      setAssociatedIds((passoc.data ?? []).map((r: any) => r.associated_product_id));

      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  const save = async () => {
    if (!sku.trim() || !slug.trim() || !translations.lv.name.trim()) {
      toast({
        title: tr("productEditor.missingFields", "Missing fields"),
        description: tr(
          "productEditor.missingFieldsDesc",
          "SKU, slug, and Latvian name are required"
        ),
        variant: "destructive",
      });
      return;
    }
    setSaving(true);

    const productPayload = {
      sku: sku.trim(),
      slug: slug.trim(),
      translations: {
        lv: stripEmpty(translations.lv),
        en: stripEmpty(translations.en),
        ru: stripEmpty(translations.ru),
      },
      abv: abv ? abv : null,
      measure_type: measureType,
      volume_ml: measureType === "volume" && volumeMl ? parseInt(volumeMl) : null,
      weight_g: measureType === "weight" && weightG ? parseInt(weightG) : null,
      measure_display: measureDisplay === "auto" ? null : measureDisplay,
      vintage: vintage ? parseInt(vintage) : null,
      country: country || null,
      region: region || null,
      producer: producer || null,
      is_alcohol: isAlcohol,
      // Age verification is driven by isAlcohol alone (owner 2026-10-02);
      // the column is kept in sync so older API consumers stay correct.
      requires_age_verification: isAlcohol,
      requires_deposit: requiresDeposit,
      active,
      featured,
      sale_price: salePrice.trim() === "" ? null : salePrice.trim(),
      barcode: barcode || null,
      serve_temp: serveTemp || null,
      pdf_url: pdfUrl || null,
      video_url: videoUrl || null,
      strength_scale: strengthScale === "" ? null : Math.max(1, Math.min(3, parseInt(strengthScale))),
      type_scale: typeScale === "" ? null : Math.max(1, Math.min(4, parseInt(typeScale))),
    };

    let productId = id;
    if (isNew) {
      const { data, error } = await supabase
        .from("products")
        .insert(productPayload)
        .select("id")
        .single();
      if (error || !data) {
        toast({
          title: tr("common.saveFailed", "Save failed"),
          description: error?.message ?? "Unknown error",
          variant: "destructive",
        });
        setSaving(false);
        return;
      }
      productId = data.id;
    } else {
      const { error } = await supabase
        .from("products")
        .update(productPayload)
        .eq("id", id!);
      if (error) {
        toast({
          title: tr("common.saveFailed", "Save failed"),
          description: error.message,
          variant: "destructive",
        });
        setSaving(false);
        return;
      }
    }

    // Replace category assignment — main category first (position 0, the
    // storefront's breadcrumb/canonical placement), the rest in tree order.
    await supabase.from("product_categories").delete().eq("product_id", productId!);
    if (categoryIds.size) {
      const ordered = [
        ...(mainCategoryId && categoryIds.has(mainCategoryId) ? [mainCategoryId] : []),
        ...[...categoryIds].filter((cid) => cid !== mainCategoryId),
      ];
      await supabase.from("product_categories").insert(
        ordered.map((cid, i) => ({
          product_id: productId,
          category_id: cid,
          position: i,
        })),
      );
    }

    // Replace prices
    await supabase.from("product_prices").delete().eq("product_id", productId!);
    const priceRows = Object.entries(prices)
      .filter(([, v]) => v !== "")
      .map(([priceListId, amount]) => ({
        product_id: productId,
        price_list_id: priceListId,
        amount,
        currency: "EUR",
      }));
    if (priceRows.length) await supabase.from("product_prices").insert(priceRows);

    // Stock is deliberately NOT written here (owner 2026-10-10): Moneo owns
    // it, and the API proxy rejects inventory_stock writes anyway.

    // Replace the product's tags (Piezīmes — image chips on the storefront).
    await supabase.from("product_tags").delete().eq("product_id", productId!);
    if (tagIds.size) {
      await supabase.from("product_tags").insert(
        Array.from(tagIds).map((tagId) => ({ product_id: productId, tag_id: tagId })),
      );
    }

    // Replace recommendation picks — sort_order preserves the pick order.
    await supabase.from("similar_products").delete().eq("product_id", productId!);
    if (similarIds.length) {
      await supabase.from("similar_products").insert(
        similarIds
          .filter((sid) => sid !== productId)
          .map((sid, i) => ({
            product_id: productId,
            similar_product_id: sid,
            sort_order: i,
          })),
      );
    }
    await supabase.from("associated_products").delete().eq("product_id", productId!);
    if (associatedIds.length) {
      await supabase.from("associated_products").insert(
        associatedIds
          .filter((aid) => aid !== productId)
          .map((aid, i) => ({
            product_id: productId,
            associated_product_id: aid,
            sort_order: i,
          })),
      );
    }

    // Replace grape variety joins
    await supabase.from("product_grape_varieties").delete().eq("product_id", productId!);
    if (grapeIds.size > 0) {
      await supabase.from("product_grape_varieties").insert(
        Array.from(grapeIds).map((grape_variety_id) => ({
          product_id: productId,
          grape_variety_id,
        }))
      );
    }

    // Replace the primary product image (one row at position 0). We don't
    // support a gallery yet — keep it simple: drop existing rows, insert
    // the new one if set. Empty string = "no image" so we just clear.
    await supabase.from("product_images").delete().eq("product_id", productId!);
    if (imageUrl.trim()) {
      await supabase.from("product_images").insert({
        product_id: productId,
        url: imageUrl.trim(),
        position: 0,
      });
    }

    toast({
      title: isNew
        ? tr("productEditor.created", "Created")
        : tr("productEditor.saved", "Saved"),
      description: tr("productEditor.savedDesc", "Product saved successfully"),
    });
    setSaving(false);
    if (isNew) navigate(`/products/${productId}`, { replace: true });
  };

  const t = translations[activeTab];
  const setT = (patch: Partial<LocaleFields>) =>
    setTranslations((prev) => ({ ...prev, [activeTab]: { ...prev[activeTab], ...patch } }));

  if (loading) {
    return (
      <div className="p-4 font-body text-sm text-muted-foreground">
        {tr("common.loading", "Loading…")}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              // Go back through history so the products list keeps its
              // search/filters/page (they live in its URL query string);
              // fall back to a plain /products when opened via deep link.
              const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
              if (idx > 0) navigate(-1);
              else navigate("/products");
            }}
            className="p-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="font-display text-xl font-bold text-foreground">
            {isNew
              ? tr("productEditor.newTitle", "New Product")
              : tr("productEditor.editTitle", "Edit Product")}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {/* Only meaningful once the product exists and has a slug; an
              inactive product 404s on the storefront, so it's flagged. */}
          {!isNew && slug.trim() && (
            <a
              href={storefrontProductUrl(slug.trim())}
              target="_blank"
              rel="noopener noreferrer"
              title={
                active
                  ? undefined
                  : tr(
                      "productEditor.viewInStoreInactive",
                      "Produkts nav aktīvs — internetveikalā tas vēl nav redzams",
                    )
              }
              className="flex items-center justify-center gap-2 rounded-lg border border-border px-4 py-2 font-body text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              <ExternalLink size={14} />
              {tr("productEditor.viewInStore", "Apskatīt produktu internetveikalā")}
              {!active && (
                <span className="font-body text-xs text-muted-foreground">
                  ({tr("products.statusInactive", "Neaktīvs")})
                </span>
              )}
            </a>
          )}
          <button
            onClick={save}
            disabled={saving || !translations.lv.name}
            className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 font-body text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            <Save size={14} />
            {saving
              ? tr("productEditor.saving", "Saving…")
              : tr("productEditor.save", "Save Product")}
          </button>
        </div>
      </div>

      {/* Body grid */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* LEFT COLUMN */}
        <div className="min-w-0 space-y-6 xl:col-span-2">
          {/* Language tabs */}
          <div className="flex items-center gap-2 border-b border-border pb-2">
            {LOCALES_KEYS.map((l) => (
              <button
                key={l.id}
                onClick={() => setActiveTab(l.id)}
                className={`rounded-t-lg px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === l.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-muted-foreground hover:bg-muted"
                }`}
              >
                {tr(l.key, l.fallback)}
              </button>
            ))}
          </div>

          {/* General Information */}
          <section className={sectionClass + " min-w-0"}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Package size={16} className="text-muted-foreground" />{" "}
              {tr("productEditor.sectionGeneral", "General Information")} ({activeTab.toUpperCase()})
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldName", "Name")} *
                </label>
                <input
                  value={t.name}
                  onChange={(e) => setT({ name: e.target.value })}
                  className={inputClass}
                  placeholder={tr("productEditor.fieldName", "Product name")}
                />
              </div>
              {activeTab !== "lv" && (
                <div>
                  <label className={labelClass}>
                    {tr("productEditor.fieldSlug", "URL slug")}{" "}
                    <span className="text-muted-foreground/60">
                      {tr("productEditor.fieldSlugHint", "(optional — falls back to the default slug)")}
                    </span>
                  </label>
                  {/* Was normalised on every keystroke, which turned "vīns"
                      into "v-ns" — SlugInput transliterates instead. */}
                  <SlugInput
                    value={t.slug}
                    onChange={(next) => setT({ slug: next })}
                    table="products"
                    excludeId={isNew ? undefined : id}
                    sourceText={t.name}
                    locale={activeTab}
                    className={inputClass + " font-mono"}
                    placeholder={slug}
                  />
                </div>
              )}
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldShortDesc", "Short description")}{" "}
                  <span className="text-muted-foreground/60">
                    {tr("productEditor.fieldShortDescHint", "(max 800 chars)")}
                  </span>
                </label>
                <textarea
                  value={t.shortDescription}
                  onChange={(e) => setT({ shortDescription: e.target.value.slice(0, 800) })}
                  maxLength={800}
                  rows={3}
                  className={inputClass + " resize-none"}
                  placeholder={tr(
                    "productEditor.fieldShortDescPlaceholder",
                    "Brief product info shown at the top of the product page..."
                  )}
                />
                <span className="mt-1 block text-right font-body text-[10px] text-muted-foreground">
                  {t.shortDescription.length}/800
                </span>
              </div>
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldDescription", "Pilnais apraksts")}
                </label>
                <textarea
                  value={t.description}
                  onChange={(e) => setT({ description: e.target.value })}
                  rows={6}
                  className={inputClass + " resize-y"}
                  placeholder={tr(
                    "productEditor.fieldDescriptionPlaceholder",
                    "Garais produkta apraksts…"
                  )}
                />
              </div>
            </div>
          </section>

          {/* Content blocks — drive the PDP tabs (Garšas piezīmes / Ražotājs /
              Vīnogas / Glabāšana / Ēdienu saderība / Apbalvojumi). Locale-
              scoped: switching tabs swaps the active language's values. */}
          <section className={sectionClass + " min-w-0"}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <BookOpen size={16} className="text-muted-foreground" />{" "}
              {tr("productEditor.sectionBlocks", "Content blocks")} ({activeTab.toUpperCase()})
            </h2>
            <p className="font-body text-xs text-muted-foreground">
              {tr(
                "productEditor.sectionBlocksHint",
                "Each block becomes its own tab on the product page when filled. Empty blocks are hidden."
              )}
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <BlockField
                label={tr("productEditor.blockSmell", "Garšas piezīmes")}
                value={t.smellDescription}
                onChange={(v) => setT({ smellDescription: v })}
                placeholder="Vaniļa, banāns, žāvēta vanele..."
              />
              <BlockField
                label={tr("productEditor.blockGrapes", "About grapes (Vīnogas)")}
                value={t.grapeDescription}
                onChange={(v) => setT({ grapeDescription: v })}
                placeholder="Šķirne, raža, vinifikācija..."
              />
              <BlockField
                label={tr("productEditor.blockStorage", "Storage (Glabāšana)")}
                value={t.storageDescription}
                onChange={(v) => setT({ storageDescription: v })}
                placeholder="Glabāt 12–14 °C, pasniegt 16 °C..."
              />
              <BlockField
                label={tr("productEditor.blockPairing", "Food pairing (Ēdienu saderība)")}
                value={t.foodPairing}
                onChange={(v) => setT({ foodPairing: v })}
                placeholder="Sieram, mājputniem, deserts..."
              />
              <div className="md:col-span-2">
                <BlockField
                  label={tr("productEditor.blockAwards", "Awards (Apbalvojumi)")}
                  value={t.awards}
                  onChange={(v) => setT({ awards: v })}
                  placeholder="Gold · IWSC 2023 · 92pt Wine Spectator"
                />
              </div>
            </div>
          </section>

          {/* SEO */}
          <section className={sectionClass + " min-w-0"}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Globe size={16} className="text-muted-foreground" />{" "}
              {tr("productEditor.sectionSeo", "Search Engine Optimization")} ({activeTab.toUpperCase()})
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldMetaTitle", "Meta title")}
                </label>
                <input
                  value={t.metaTitle}
                  onChange={(e) => setT({ metaTitle: e.target.value })}
                  className={inputClass}
                  placeholder={tr(
                    "productEditor.fieldMetaTitlePlaceholder",
                    "Page <title> for search engines"
                  )}
                />
              </div>
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldMetaDesc", "Meta description")}
                </label>
                <textarea
                  value={t.metaDescription}
                  onChange={(e) => setT({ metaDescription: e.target.value })}
                  rows={2}
                  className={inputClass + " resize-none"}
                  placeholder={tr(
                    "productEditor.fieldMetaDescPlaceholder",
                    "Meta description for search engines"
                  )}
                />
              </div>
            </div>
          </section>

          {/* Media — locale-agnostic. One product image (relational
            product_images row, position 0), plus the tech-sheet PDF and
            promo video URLs (moved out of Beverage Metadata so they sit
            alongside the image). Same pattern as miluliem's Media
            section but lifted up to the lv-only tab so the inputs aren't
            duplicated across language tabs. */}
          {activeTab === "lv" && (
            <section className={sectionClass + " min-w-0"}>
              <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
                <ImageIcon size={16} className="text-muted-foreground" />{" "}
                {tr("productEditor.sectionMedia", "Media")}
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className={labelClass}>
                    {tr("productEditor.fieldImage", "Product image")}
                  </label>
                  <ImageUpload
                    value={imageUrl}
                    onChange={setImageUrl}
                    bucket="products"
                    placeholder={tr(
                      "productEditor.fieldImagePlaceholder",
                      "Drop product image here"
                    )}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {tr("productEditor.fieldPdfUrl", "Tehniskā lapa (PDF)")}
                  </label>
                  <FileUpload
                    value={pdfUrl}
                    onChange={setPdfUrl}
                    bucket="documents"
                    accept="pdf"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {tr("productEditor.fieldVideoUrl", "Video URL")}
                  </label>
                  <FileUpload
                    value={videoUrl}
                    onChange={setVideoUrl}
                    bucket="documents"
                    accept="video"
                  />
                </div>
              </div>
            </section>
          )}

          {/* Beverage metadata */}
          <section className={sectionClass + " min-w-0"}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Wine size={16} className="text-muted-foreground" />{" "}
              {tr("productEditor.sectionBeverage", "Beverage Metadata")}
            </h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldAbv", "ABV %")}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={abv}
                  onChange={(e) => setAbv(e.target.value)}
                  className={inputClass}
                  placeholder="13.50"
                />
              </div>
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldMeasureType", "Mērvienība")}
                </label>
                <Select
                  value={measureType}
                  onValueChange={(v) => {
                    setMeasureType(v as "volume" | "weight");
                    // Display options differ per type — reset to automatic.
                    setMeasureDisplay("auto");
                  }}
                >
                  <SelectTrigger className={inputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="volume">
                      {tr("productEditor.measureVolume", "Tilpums")}
                    </SelectItem>
                    <SelectItem value="weight">
                      {tr("productEditor.measureWeight", "Svars")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelClass}>
                  {measureType === "weight"
                    ? tr("productEditor.fieldWeight", "Svars (g)")
                    : tr("productEditor.fieldVolume", "Tilpums (ml)")}
                </label>
                <input
                  type="number"
                  min={0}
                  value={measureType === "weight" ? weightG : volumeMl}
                  onChange={(e) =>
                    measureType === "weight"
                      ? setWeightG(e.target.value)
                      : setVolumeMl(e.target.value)
                  }
                  className={inputClass}
                  placeholder={measureType === "weight" ? "250" : "750"}
                />
              </div>
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldMeasureDisplay", "Attēlot internetveikalā kā")}
                </label>
                <Select value={measureDisplay} onValueChange={setMeasureDisplay}>
                  <SelectTrigger className={inputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">
                      {measureType === "weight"
                        ? tr("productEditor.measureAutoWeight", "Automātiski (g / kg)")
                        : tr("productEditor.measureAutoVolume", "Automātiski (cl / l)")}
                    </SelectItem>
                    {(measureType === "weight" ? ["kg", "g"] : ["l", "cl", "ml"]).map((u) => (
                      <SelectItem key={u} value={u}>
                        {u}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="mt-1 block font-body text-[10px] text-muted-foreground">
                  {measureType === "weight"
                    ? tr("productEditor.unitPriceWeight", "Vienības cena: € / kg")
                    : tr("productEditor.unitPriceVolume", "Vienības cena: € / l")}
                </span>
              </div>
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldVintage", "Vintage")}
                </label>
                <input
                  type="number"
                  value={vintage}
                  onChange={(e) => setVintage(e.target.value)}
                  className={inputClass}
                  placeholder="2018"
                />
              </div>
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldCountry", "Country")}
                </label>
                <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      role="combobox"
                      aria-expanded={countryOpen}
                      className={cn(
                        inputClass,
                        "flex items-center justify-between text-left",
                        !country && "text-muted-foreground"
                      )}
                    >
                      {country ||
                        tr("productEditor.fieldCountryPlaceholder", "Select country...")}
                      <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[260px] p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder={tr(
                          "productEditor.fieldCountrySearch",
                          "Search country..."
                        )}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {tr("productEditor.fieldCountryEmpty", "No match.")}
                        </CommandEmpty>
                        <CommandGroup>
                          {COUNTRY_NAMES_LV.map((c) => (
                            <CommandItem
                              key={c}
                              value={c}
                              onSelect={(value) => {
                                setCountry(value === country ? "" : value);
                                setCountryOpen(false);
                              }}
                            >
                              <Check
                                size={14}
                                className={cn(
                                  "mr-2",
                                  country === c ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {c}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldRegion", "Region")}
                </label>
                <Popover open={regionOpen} onOpenChange={setRegionOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      role="combobox"
                      aria-expanded={regionOpen}
                      className={cn(
                        inputClass,
                        "flex items-center justify-between text-left",
                        !region && "text-muted-foreground"
                      )}
                    >
                      {region ||
                        tr("productEditor.fieldRegionPlaceholder", "Select region...")}
                      <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[260px] p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder={tr(
                          "productEditor.fieldRegionSearch",
                          "Search region..."
                        )}
                        value={regionQuery}
                        onValueChange={setRegionQuery}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {tr("productEditor.fieldRegionEmpty", "No match.")}
                        </CommandEmpty>
                        <CommandGroup>
                          {regionOptions.map((r) => (
                            <CommandItem
                              key={r}
                              value={r}
                              onSelect={(value) => {
                                setRegion(value === region ? "" : value);
                                setRegionOpen(false);
                                setRegionQuery("");
                              }}
                            >
                              <Check
                                size={14}
                                className={cn(
                                  "mr-2",
                                  region === r ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {r}
                            </CommandItem>
                          ))}
                          {/* Free-text fallback: regions are open-ended (the
                            catalog list is just popular ones), so allow the
                            user to commit any typed value that isn't in the
                            list. The cmdk fuzzy match hides this item when
                            the query exactly matches an existing option. */}
                          {regionQuery.trim().length > 0 &&
                            !regionOptions.some(
                              (r) =>
                                r.toLowerCase() === regionQuery.trim().toLowerCase()
                            ) && (
                              <CommandItem
                                value={`__custom__${regionQuery}`}
                                onSelect={() => {
                                  setRegion(regionQuery.trim());
                                  setRegionOpen(false);
                                  setRegionQuery("");
                                }}
                              >
                                <Check size={14} className="mr-2 opacity-0" />
                                {tr("productEditor.fieldRegionUseCustom", "Use")}
                                {' "'}
                                {regionQuery.trim()}
                                {'"'}
                              </CommandItem>
                            )}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldProducer", "Producer")}
                </label>
                <input
                  value={producer}
                  onChange={(e) => setProducer(e.target.value)}
                  className={inputClass}
                  placeholder="Château ..."
                />
              </div>
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldBarcode", "Barcode (EAN)")}
                </label>
                <input
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  className={inputClass}
                  placeholder="5203021111105"
                />
              </div>
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldServeTemp", "Serving temp. (°C)")}
                </label>
                <input
                  value={serveTemp}
                  onChange={(e) => setServeTemp(e.target.value)}
                  className={inputClass}
                  placeholder="8–10"
                />
              </div>
              <ScaleField
                label={tr("productEditor.fieldStrengthScale", "Uzbūve")}
                value={strengthScale}
                onChange={setStrengthScale}
                levels={UZBUVE_LEVELS}
              />
              <ScaleField
                label={tr("productEditor.fieldTypeScale", "Saldums")}
                value={typeScale}
                onChange={setTypeScale}
                levels={SALDUMS_LEVELS}
              />
            </div>

            {/* Grape varieties — multi-select with chips. Sources active grapes
                from grape_varieties; admin can add new ones in /grape-varieties. */}
            <div className="border-t border-border pt-4">
              <label className={labelClass}>
                {tr("productEditor.fieldGrapes", "Grape varieties")}
              </label>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {Array.from(grapeIds).map((gid) => {
                  const g = grapeOptions.find((o) => o.id === gid);
                  if (!g) return null;
                  return (
                    <button
                      key={gid}
                      type="button"
                      onClick={() => {
                        const next = new Set(grapeIds);
                        next.delete(gid);
                        setGrapeIds(next);
                      }}
                      className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 font-body text-xs font-medium text-primary hover:bg-primary/20"
                    >
                      {g.translations?.lv?.name ?? g.slug}
                      <span aria-hidden>×</span>
                    </button>
                  );
                })}
                {grapeIds.size === 0 && (
                  <span className="font-body text-xs text-muted-foreground">
                    {tr("productEditor.noGrapesYet", "No grapes assigned")}
                  </span>
                )}
              </div>
              <Select
                value=""
                onValueChange={(v) => {
                  if (!v) return;
                  setGrapeIds(new Set(grapeIds).add(v));
                }}
              >
                <SelectTrigger className={inputClass}>
                  <SelectValue
                    placeholder={tr("productEditor.addGrape", "Add grape variety…")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {grapeOptions
                    .filter((g) => !grapeIds.has(g.id))
                    .map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.translations?.lv?.name ?? g.slug}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </section>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6">
          {/* Status & Availability */}
          <section className={sectionClass}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <CheckCircle2 size={16} className="text-muted-foreground" />{" "}
              {tr("productEditor.sectionStatus", "Status & Availability")}
            </h2>
            <div className="space-y-2">
              <ToggleRow
                label={tr("productEditor.toggleActive", "Active")}
                hint={tr(
                  "productEditor.toggleActiveHint",
                  "Visible on storefront when active"
                )}
                checked={active}
                onChange={setActive}
              />
              <ToggleRow
                label={tr("productEditor.toggleFeatured", "Featured")}
                hint={tr(
                  "productEditor.toggleFeaturedHint",
                  "Highlighted in featured product sections"
                )}
                checked={featured}
                onChange={setFeatured}
              />
              <ToggleRow
                label={tr("productEditor.toggleIsAlcohol", "Is alcohol")}
                hint={tr(
                  "productEditor.toggleIsAlcoholHint",
                  "Vecuma pārbaude (18+ un Dokobit), tirdzniecības laika ierobežojums, 6 h saņemšanas noteikums un kategoriju izvietojums",
                )}
                checked={isAlcohol}
                onChange={setIsAlcohol}
              />
              <ToggleRow
                label={tr("productEditor.toggleDeposit", "Requires deposit")}
                hint={tr(
                  "productEditor.toggleDepositHint",
                  "Adds €0.10 (excl. VAT) per bottle to checkout total"
                )}
                checked={requiresDeposit}
                onChange={setRequiresDeposit}
              />
            </div>
          </section>

          {/* Piezīmes — the product's tags (curated in /tags), shown as
              colored chips on the product image in the storefront. */}
          <section className={sectionClass}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Tag size={16} className="text-muted-foreground" />{" "}
              {tr("productEditor.sectionLabels", "Piezīmes (birkas)")}
            </h2>
            <p className="font-body text-xs text-muted-foreground">
              {tr(
                "productEditor.labelsHint",
                "Izvēlies no birkām, kas definētas sadaļā Birkas (/tags) — izvēlētās parādās kā uzlīmes uz produkta attēla internetveikalā (rādām ne vairāk kā 3).",
              )}
            </p>
            {tagOptions.length === 0 ? (
              <p className="font-body text-xs text-muted-foreground">
                {tr("productEditor.noTags", "Nav nevienas aktīvas birkas. Izveido tās sadaļā Birkas.")}
              </p>
            ) : (
              <div className="space-y-2">
                {tagOptions.map((tag) => (
                  <label
                    key={tag.id}
                    className="flex cursor-pointer items-center gap-2 font-body text-sm text-foreground"
                  >
                    <input
                      type="checkbox"
                      checked={tagIds.has(tag.id)}
                      onChange={(e) =>
                        setTagIds((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(tag.id);
                          else next.delete(tag.id);
                          return next;
                        })
                      }
                      className="h-4 w-4 accent-primary"
                    />
                    <span
                      className="inline-block h-3 w-3 shrink-0 rounded-full border border-border"
                      style={{ background: tag.color ?? "var(--muted)" }}
                    />
                    {tag.name}
                  </label>
                ))}
              </div>
            )}
          </section>

          {/* Identity */}
          <section className={sectionClass}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Tag size={16} className="text-muted-foreground" />{" "}
              {tr("productEditor.sectionIdentity", "Identity")}
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldSku", "SKU")} *
                </label>
                <input
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  className={inputClass + " font-mono"}
                  placeholder="VIN-RED-001"
                />
              </div>
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldSlug", "Slug")} *
                </label>
                <SlugInput
                  value={slug}
                  onChange={setSlug}
                  table="products"
                  excludeId={isNew ? undefined : id}
                  sourceText={translations.lv.name}
                  className={inputClass + " font-mono"}
                />
              </div>
            </div>
          </section>

          {/* Organization */}
          <section className={sectionClass}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Tag size={16} className="text-muted-foreground" />{" "}
              {tr("productEditor.sectionOrganization", "Organization")}
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>
                  {tr("productEditor.fieldCategories", "Kategorijas")}
                </label>
                <CategoryTreePicker
                  categories={categories}
                  selected={categoryIds}
                  mainId={mainCategoryId}
                  onChange={(sel, main) => {
                    setCategoryIds(sel);
                    setMainCategoryId(main);
                  }}
                />
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Pricing & Inventory (full width) */}
      <div className="w-full">
        <section className={sectionClass + " overflow-hidden min-w-0 relative"}>
          <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
            <Layers size={16} className="text-muted-foreground" />{" "}
            {tr("productEditor.sectionPricingInventory", "Pricing & Inventory")}
          </h2>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
              <h3 className="mb-3 border-b border-border pb-2 font-display text-sm font-semibold text-foreground">
                {tr("productEditor.sectionPricingInventory", "Pricing & Inventory")} (EUR)
              </h3>
              <div className="space-y-3">
                {priceLists.map((pl) => (
                  <div key={pl.id} className="flex items-center gap-3">
                    <label className="w-32 shrink-0 font-body text-xs text-muted-foreground">
                      {pl.name}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={prices[pl.id] ?? ""}
                      onChange={(e) =>
                        setPrices((prev) => ({ ...prev, [pl.id]: e.target.value }))
                      }
                      className={inputClass}
                      placeholder="0.00"
                    />
                  </div>
                ))}
                {priceLists.length === 0 && (
                  <p className="font-body text-xs text-muted-foreground">
                    {tr(
                      "productEditor.priceListEmpty",
                      "No price lists configured yet."
                    )}
                  </p>
                )}

                {/* Promotional sale price — a discount off the base price shown
                  as a strikethrough on the storefront and charged in the cart.
                  Distinct from the customer-tier price lists above. */}
                <div className="mt-4 border-t border-border pt-4">
                  <label className="mb-1 block font-body text-xs font-semibold text-foreground">
                    {tr("productEditor.salePrice", "Sale price (Akcijas cena)")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={salePrice}
                    onChange={(e) => setSalePrice(e.target.value)}
                    className={inputClass}
                    placeholder={tr("productEditor.salePricePlaceholder", "No discount")}
                  />
                  <p className="mt-1 font-body text-xs text-muted-foreground">
                    {tr(
                      "productEditor.salePriceHint",
                      "Discounted price off the base price. Leave empty for no sale. Must be lower than the base price to take effect."
                    )}
                  </p>
                  {(() => {
                    const baseId = priceLists.find((pl) => pl.code === "base")?.id;
                    const baseVal = baseId ? Number(prices[baseId] ?? "") : NaN;
                    const saleVal = Number(salePrice);
                    if (
                      salePrice.trim() !== "" &&
                      Number.isFinite(saleVal) &&
                      Number.isFinite(baseVal) &&
                      saleVal >= baseVal
                    ) {
                      return (
                        <p className="mt-1 font-body text-xs text-destructive">
                          {tr(
                            "productEditor.salePriceTooHigh",
                            "Sale price is not lower than the base price — it will be ignored."
                          )}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </div>

            <div>
              <h3 className="mb-3 border-b border-border pb-2 font-display text-sm font-semibold text-foreground">
                {tr("nav.inventory", "Stock by location")}
              </h3>
              {/* Read-only (owner 2026-10-10): stock is owned by Moneo — it
                  holds every POS transaction and the e-com orders we push,
                  so hand-edits here only drift from the source of truth.
                  The API proxy also rejects inventory_stock writes. */}
              <div className="space-y-2">
                {locations.map((loc) => (
                  <div key={loc.id} className="flex items-center justify-between gap-3">
                    <span className="font-body text-xs text-muted-foreground">
                      {loc.name}{" "}
                      <span className="text-muted-foreground/60">({loc.code})</span>
                    </span>
                    <span className="font-body text-sm font-medium text-foreground">
                      {stock[loc.id] ?? "0"}
                    </span>
                  </div>
                ))}
                {locations.length === 0 && (
                  <p className="font-body text-xs text-muted-foreground">
                    {tr("productEditor.stockEmpty", "No inventory locations yet.")}
                  </p>
                )}
                <p className="border-t border-border pt-2 font-body text-xs text-muted-foreground">
                  {tr(
                    "productEditor.stockManagedByMoneo",
                    "Atlikumus pārvalda Moneo (POS darījumi + e-veikala pasūtījumi) — panelī tie nav rediģējami.",
                  )}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Recommendations (full width) — Sept 9 report #11 */}
      <div className="w-full">
        <section className={sectionClass + " min-w-0"}>
          <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
            <Wine size={16} className="text-muted-foreground" />{" "}
            {tr("productEditor.sectionRecommendations", "Ieteikumi")}
          </h2>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <RecommendationPicker
              label={tr("productEditor.similarProducts", "Līdzīgie produkti")}
              hint={tr(
                "productEditor.similarProductsHint",
                "Alternatīvas, ko rādīt produkta lapā. Ja saraksts tukšs, veikals rāda tās pašas kategorijas populārākos."
              )}
              selected={similarIds}
              onChange={setSimilarIds}
              options={pickerProducts}
              excludeId={isNew ? null : id ?? null}
            />
            <RecommendationPicker
              label={tr("productEditor.associatedProducts", "Pērk kopā (grozs / checkout)")}
              hint={tr(
                "productEditor.associatedProductsHint",
                "Papildu produkti, ko piedāvāt grozā un checkout, kad šis produkts ir grozā."
              )}
              selected={associatedIds}
              onChange={setAssociatedIds}
              options={pickerProducts}
              excludeId={isNew ? null : id ?? null}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function RecommendationPicker({
  label,
  hint,
  selected,
  onChange,
  options,
  excludeId,
}: {
  label: string;
  hint: string;
  selected: string[];
  onChange: (ids: string[]) => void;
  options: Array<{ id: string; sku: string; name: string }>;
  excludeId: string | null;
}) {
  const [query, setQuery] = useState("");
  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const q = query.trim().toLowerCase();
  const matches =
    q.length < 2
      ? []
      : options
          .filter(
            (o) =>
              o.id !== excludeId &&
              !selected.includes(o.id) &&
              (o.name.toLowerCase().includes(q) || o.sku.toLowerCase().includes(q)),
          )
          .slice(0, 8);
  return (
    <div>
      <h3 className="mb-1 font-display text-sm font-semibold text-foreground">{label}</h3>
      <p className="mb-3 font-body text-xs text-muted-foreground">{hint}</p>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {selected.map((sid) => {
          const o = byId.get(sid);
          return (
            <button
              key={sid}
              type="button"
              onClick={() => onChange(selected.filter((x) => x !== sid))}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 font-body text-xs font-medium text-primary hover:bg-primary/20"
            >
              {o ? `${o.name} (${o.sku})` : sid}
              <span aria-hidden>×</span>
            </button>
          );
        })}
        {selected.length === 0 && (
          <span className="font-body text-xs text-muted-foreground/60">—</span>
        )}
      </div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className={inputClass}
        placeholder="Meklēt pēc nosaukuma vai SKU…"
      />
      {matches.length > 0 && (
        <div className="mt-1 rounded-lg border border-border bg-card shadow-sm">
          {matches.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => {
                onChange([...selected, o.id]);
                setQuery("");
              }}
              className="block w-full px-3 py-2 text-left font-body text-xs text-foreground hover:bg-muted"
            >
              {o.name} <span className="text-muted-foreground">({o.sku})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
      <div>
        <p className="font-body text-sm font-medium text-foreground">{label}</p>
        {hint && <p className="mt-0.5 font-body text-xs text-muted-foreground">{hint}</p>}
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 cursor-pointer rounded-sm border-border text-primary focus:ring-primary"
      />
    </div>
  );
}

function stripEmpty<T extends Record<string, string>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v && v.trim() !== "") (out as Record<string, string>)[k] = v;
  }
  return out;
}

function BlockField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className={inputClass + " resize-y"}
        placeholder={placeholder}
      />
    </div>
  );
}

// Discrete scale levels (Sept 9 report #4). "none" clears the value — the
// product then shows no scale and is excluded from that filter.
export const UZBUVE_LEVELS: Array<{ value: number; label: string }> = [
  { value: 1, label: "Viegls" },
  { value: 2, label: "Vidēji pilnmiesīgs" },
  { value: 3, label: "Pilnmiesīgs" },
];
export const SALDUMS_LEVELS: Array<{ value: number; label: string }> = [
  { value: 1, label: "Sauss" },
  { value: 2, label: "Pussauss" },
  { value: 3, label: "Pussalds" },
  { value: 4, label: "Salds" },
];

function ScaleField({
  label,
  value,
  onChange,
  levels,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  levels: Array<{ value: number; label: string }>;
}) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <Select
        value={value === "" ? "none" : value}
        onValueChange={(v) => onChange(v === "none" ? "" : v)}
      >
        <SelectTrigger>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">—</SelectItem>
          {levels.map((lvl) => (
            <SelectItem key={lvl.value} value={String(lvl.value)}>
              {lvl.value} · {lvl.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
